'use client';

import React from 'react';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerInvoiceDocument } from '@packages/gql/generated/shopping/CustomerInvoiceDocument';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { Link } from '@packages/modules/src/Link/Link';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { Locale } from '@packages/config';
import { parseBackendMessage } from '@packages/modules/src/BackendMessageParser/BackendMessageParser';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import type { CustomerInvoiceQuery } from '@packages/gql/generated/shopping/graphql';
import { messages } from './messages';
import { AccountInformation } from '../AccountInformation/AccountInformation';
import ReclamationForm from '../ReclamationForm/ReclamationForm.client';
import { InvoiceDownloadButton } from './InvoiceDownloadButton.client';

/** Invoice, product and reimbursement price modifications select the same fields. */
type PriceModification = NonNullable<
  NonNullable<CustomerInvoiceQuery['customerInvoice']['invoice']>['priceModifications']
>[number];

const getPriceModificationLabel = (label: PriceModification['name']) =>
  label.endsWith(':') ? label : `${label}:`;

type FormatPrice = ReturnType<typeof useFormatPrice>['formatPrice'];

const getPriceModificationSuffix = (modification: PriceModification) =>
  `price-mod-${modification.name}-${modification.isNegative ? 'neg' : 'pos'}-${modification.value.value}-${modification.value.currencyCode}`;

const getPriceModificationKey = (productId: string, modification: PriceModification) =>
  `${productId}-${getPriceModificationSuffix(modification)}`;

const formatSignedPrice = (
  formatPrice: FormatPrice,
  value: PriceModification['value'],
  isNegative: PriceModification['isNegative'],
) => `${isNegative ? '-' : '+'}${formatPrice(value.value, value.currencyCode)}`;

/* GraphQL */ `
  query CustomerInvoice($customerInvoiceId: String!) {
    customerInvoice(id: $customerInvoiceId) {
      invoice {
        invoiceId
        invoiceNumber
        invoiceDate
        status
        customerNumber
        invoiceAddress {
          birthName
          lastName
          firstName
          zipCode
          houseNumber
          countryCode
          city
          additional
          addressName
          honorific
          gender
          primaryPhoneNumber
          secondaryPhoneNumber
          street
        }
        deliveryAddress {
          birthName
          lastName
          firstName
          zipCode
          houseNumber
          countryCode
          city
          additional
          addressName
          honorific
          gender
          primaryPhoneNumber
          secondaryPhoneNumber
          street
        }
        paymentMethod
        paymentIntendedUse
        subTotal {
          currencyCode
          value
        }
        priceModifications {
          name
          value {
            currencyCode
            value
          }
          isNegative
        }
        grandTotal {
          currencyCode
          value
        }
        paymentHint
        paymentPlan {
          text
        }
        products {
          id
          orderNumber
          quantity
          name
          variation
          price {
            currencyCode
            value
          }
          finalPrice {
            currencyCode
            value
          }
          oldPrice {
            currencyCode
            value
          }
          paymentMethod
          installmentCount
          isNali
          url
          imageUrl
          extra {
            orderNumber
            brand
            category
          }
          aplus {
            name
            finalPrice {
              currencyCode
              value
            }
          }
          priceModifications {
            name
            value {
              currencyCode
              value
            }
            isNegative
          }
        }
        reimbursements {
          entries {
            name
            orderNumber
            quantity
            variation
            imageUrl
            dateFormatted
            amount {
              currencyCode
              value
            }
          }
          priceModifications {
            name
            value {
              currencyCode
              value
            }
            isNegative
          }
          total {
            currencyCode
            value
          }
        }
        showEffectiveInstallmentsHint
        showBankConnection
      }
    }
  }
`;

export const InvoiceDetail = ({
  onClose,
  invoiceNumber,
}: {
  onClose: () => void;
  invoiceNumber: string;
}): React.ReactElement | null => {
  const [{ data, error, fetching }] = useAuthQuery({
    query: CustomerInvoiceDocument,
    variables: {
      customerInvoiceId: invoiceNumber,
    },
    requestPolicy: 'cache-and-network',
  });
  const [reclamationState, setReclamationState] = React.useState({
    open: false,
    productOrderNumber: '',
    productName: '',
  });
  const { formatMessage, locale } = useIntl();
  const { formatPrice } = useFormatPrice();
  const config = useConfig();
  const isDesktop = useMediaType();
  const {
    legalName,
    location: {
      address: { streetAddress, postalCode, addressLocality },
    },
  } = config.tracking.schemaOrgOrganisation[locale as Locale];

  const invoice = data?.customerInvoice?.invoice;

  const isProductReturned = (productId: string) =>
    invoice?.reimbursements?.entries.some((entry) => entry.orderNumber === productId);

  // amount left to pay once the credit note (incl. a reversed voucher) is settled against the invoice
  const openAmount =
    invoice?.grandTotal && invoice.reimbursements
      ? invoice.grandTotal.value - invoice.reimbursements.total.value
      : null;

  // Render loading state content
  const renderLoadingContent = () => (
    <div className="p-1.5">
      <div className="flex gap-2">
        <Skeleton className="mb-2 h-6 w-[10rem]" />
        <Skeleton className="mb-2 h-6 w-[10rem]" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="mb-2 h-6 w-[10rem]" />
        <Skeleton className="mb-2 h-6 w-[10rem]" />
      </div>
      <Skeleton className="mb-2 h-[5rem] w-full" />
      <Skeleton className="mb-2 h-[5rem] w-full" />
      <Skeleton className="mb-2 h-[5rem] w-full" />
    </div>
  );

  // Render error state content
  const renderErrorContent = () => (
    <div className="p-1.5">
      <Notification severity="error" className="my-2">
        <FormattedMessage {...messages.loadError} />
      </Notification>
    </div>
  );

  // Render invoice not found error (when BE returns NULL)
  const renderInvoiceNotFoundError = () => (
    <div className="p-1.5">
      <Notification severity="warning" className="my-2">
        <FormattedMessage {...messages.invoiceNotFoundError} />
      </Notification>
    </div>
  );

  // Render success state content
  const renderInvoiceContent = () => {
    if (!invoice) return null;

    return (
      <>
        {reclamationState.open && (
          <ReclamationForm
            invoiceAddress={invoice.invoiceAddress}
            deliveryAddress={invoice.deliveryAddress}
            customerNumber={invoice.customerNumber}
            invoiceNumber={invoice.invoiceNumber}
            invoiceDate={invoice.invoiceDate}
            productOrderNumber={reclamationState.productOrderNumber}
            productName={reclamationState.productName}
            onClose={() =>
              setReclamationState({ open: false, productOrderNumber: '', productName: '' })
            }
          />
        )}
        <div className="p-1.5">
          <div className="mb-2.5 grid grid-cols-2 gap-x-2 gap-y-1">
            {/* Invoice metadata */}
            <span className="font-bold">{formatMessage(messages.dateLabel)}</span>
            <span>{invoice.invoiceDate}</span>

            <span className="font-bold">{formatMessage(messages.invoiceNumberLabel)}</span>
            <span>{invoice.invoiceNumber}</span>

            <span className="font-bold">{formatMessage(messages.customerNumberLabel)}</span>
            <span>{invoice.customerNumber}</span>

            <span className="font-bold">{formatMessage(messages.paymentMethodLabel)}</span>
            <span>{invoice.paymentMethod}</span>

            {/* Address headers with extra spacing */}
            <span className="mt-1.5 font-bold">{formatMessage(messages.deliveryAddressLabel)}</span>
            <span className="mt-1.5 font-bold">{formatMessage(messages.invoiceAddressLabel)}</span>

            {/* Address data - each logical group on its own line */}
            <div className="flex flex-col">
              <div>
                {invoice.deliveryAddress.gender} {invoice.deliveryAddress.honorific}
              </div>
              <div>
                {invoice.deliveryAddress.firstName} {invoice.deliveryAddress.lastName}
              </div>
              <div>
                {invoice.deliveryAddress.street} {invoice.deliveryAddress.houseNumber}
              </div>
              <div>
                {invoice.deliveryAddress.zipCode} {invoice.deliveryAddress.city}
              </div>
            </div>
            <div className="flex flex-col">
              <div>
                {invoice.invoiceAddress.gender} {invoice.invoiceAddress.honorific}
              </div>
              <div>
                {invoice.invoiceAddress.firstName} {invoice.invoiceAddress.lastName}
              </div>
              <div>
                {invoice.invoiceAddress.street} {invoice.invoiceAddress.houseNumber}
              </div>
              <div>
                {invoice.invoiceAddress.zipCode} {invoice.invoiceAddress.city}
              </div>
            </div>
          </div>

          {/* Single parent grid containing header, products, and totals - hidden on mobile */}
          {isDesktop && (
            <div className="grid grid-cols-[auto_2fr_1fr_1fr]">
              {/* Header row */}
              <div className="bg-color-text-dark text-color-text-light col-span-2 mr-px px-1.5 py-1 font-bold">
                {formatMessage(messages.articleHeader)}
              </div>
              <div className="bg-color-text-dark text-color-text-light col-span-2 px-1.5 py-1 font-bold">
                {formatMessage(messages.invoiceHeader)}
              </div>

              {/* Product rows */}
              {invoice.products.map((product, index) => (
                <React.Fragment key={product.id}>
                  {/* Column 1: Image */}
                  <div className={`px-1.5 py-2 ${index % 2 === 0 ? 'bg-color-grey-light' : ''}`}>
                    <div className="h-20 w-20">
                      <Image
                        width={72}
                        height={72}
                        src={product.imageUrl || ''}
                        alt={product.name}
                      />
                    </div>
                  </div>

                  {/* Column 2: Product info */}
                  <div
                    className={`mr-px flex min-w-0 flex-col gap-0.5 py-2 pr-1.5 ${index % 2 === 0 ? 'bg-color-grey-light' : ''}`}
                  >
                    <span className="font-bold wrap-break-word">{product.name}</span>
                    <span className="text-size-body-sm">
                      {formatMessage(messages.articleNumberLabel)} {product.orderNumber}
                    </span>
                    {product.variation && (
                      <span className="text-size-body-sm">{product.variation}</span>
                    )}
                    <span className="text-size-body-sm">
                      {formatMessage(messages.quantityLabel)} {product.quantity}
                    </span>
                    <span className="text-size-body-sm">
                      {formatMessage(messages.paymentMethodLabel)} {product.paymentMethod}
                    </span>
                    {product.orderNumber && isProductReturned(product.orderNumber) && (
                      <span className="text-size-body-sm">
                        {formatMessage(messages.productReturned)}
                      </span>
                    )}
                    <Button
                      variant="link"
                      className="justify-start self-start p-0 text-left"
                      onClick={() =>
                        setReclamationState({
                          open: true,
                          productOrderNumber: product.orderNumber || '',
                          productName: product.name,
                        })
                      }
                    >
                      {formatMessage(messages.reportProblemButton)}
                    </Button>
                  </div>

                  {/* Columns 3 & 4: Pricing - nested grid */}
                  <div
                    className={`text-size-body-sm col-span-2 grid grid-cols-[auto_auto] content-start gap-x-2 gap-y-0.5 px-1.5 py-2 ${index % 2 === 0 ? 'bg-color-grey-light' : ''}`}
                  >
                    <span className="text-right">{formatMessage(messages.itemValueLabel)}</span>
                    <span className="text-right">
                      {formatPrice(
                        (product.oldPrice ?? product.price).value,
                        (product.oldPrice ?? product.price).currencyCode,
                      )}
                    </span>

                    {product.priceModifications?.map((modification) => (
                      <React.Fragment key={getPriceModificationKey(product.id, modification)}>
                        <span className="text-right">
                          {getPriceModificationLabel(modification.name)}
                        </span>
                        <span className="text-right">
                          {formatSignedPrice(
                            formatPrice,
                            modification.value,
                            modification.isNegative,
                          )}
                        </span>
                      </React.Fragment>
                    ))}

                    {product.aplus &&
                      product.aplus.length > 0 &&
                      product.aplus.map((service) => (
                        <React.Fragment key={service.name}>
                          <span className="text-right">{service.name}</span>
                          <span className="text-right">
                            {formatPrice(service.finalPrice.value, service.finalPrice.currencyCode)}
                          </span>
                        </React.Fragment>
                      ))}

                    <span className="text-right font-bold">
                      {formatMessage(messages.endPriceLabel)}
                    </span>
                    <span className="text-right font-bold">
                      {formatPrice(product.finalPrice?.value, product.finalPrice?.currencyCode)}
                    </span>
                  </div>
                </React.Fragment>
              ))}

              {/* Divider row spanning all columns */}
              <div className="col-span-4 my-1 px-1.5">
                <Divider />
              </div>

              {/* Totals section */}
              <div className="col-span-3 mb-0.5 px-1.5 text-right font-bold">
                {formatMessage(messages.title)}
              </div>
              <div />

              <div className="col-span-3 px-1.5 text-right">
                {formatMessage(messages.subtotalLabel)}
              </div>
              <div className="pr-1.5 text-right">
                {formatPrice(invoice.subTotal?.value, invoice.subTotal?.currencyCode)}
              </div>

              {invoice.priceModifications?.map((modification) => (
                <React.Fragment key={modification.name}>
                  <div className="col-span-3 px-1.5 text-right">{modification.name}</div>
                  <div className="pr-1.5 text-right">
                    {modification.isNegative ? '-' : '+'}{' '}
                    {formatPrice(modification.value?.value, modification.value?.currencyCode)}
                  </div>
                </React.Fragment>
              ))}

              <div className="col-span-3 px-1.5 text-right font-bold">
                {formatMessage(messages.grandTotalLabel)}
              </div>
              <div className="pr-1.5 text-right font-bold">
                {formatPrice(invoice.grandTotal?.value, invoice.grandTotal?.currencyCode)}
              </div>
            </div>
          )}

          {/* Mobile layout - stacked */}
          {!isDesktop && (
            <div>
              {invoice.products.map((product, index) => (
                <div
                  key={product.id}
                  className={`px-1.5 py-2 ${index % 2 === 0 ? 'bg-color-grey-light' : ''}`}
                >
                  <div className="flex gap-2">
                    <div className="h-20 w-20 shrink-0">
                      <Image
                        width={72}
                        height={72}
                        src={product.imageUrl || ''}
                        alt={product.name}
                      />
                    </div>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="font-bold wrap-break-word">{product.name}</span>
                      <span className="text-size-body-sm">
                        {formatMessage(messages.articleNumberLabel)} {product.orderNumber}
                      </span>
                      {product.variation && (
                        <span className="text-size-body-sm">{product.variation}</span>
                      )}
                      <span className="text-size-body-sm">
                        {formatMessage(messages.quantityLabel)} {product.quantity}
                      </span>
                      <span className="text-size-body-sm">
                        {formatMessage(messages.paymentMethodLabel)} {product.paymentMethod}
                      </span>
                      {product.orderNumber && isProductReturned(product.orderNumber) && (
                        <span className="text-size-body-sm">
                          {formatMessage(messages.productReturned)}
                        </span>
                      )}
                      <Button
                        variant="link"
                        className="justify-start self-start p-0 text-left"
                        onClick={() =>
                          setReclamationState({
                            open: true,
                            productOrderNumber: product.orderNumber || '',
                            productName: product.name,
                          })
                        }
                      >
                        {formatMessage(messages.reportProblemButton)}
                      </Button>
                    </div>
                  </div>

                  <div className="text-size-body-sm mt-2 grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5">
                    <span>{formatMessage(messages.itemValueLabel)}</span>
                    <span className="text-right">
                      {formatPrice(
                        (product.oldPrice ?? product.price).value,
                        (product.oldPrice ?? product.price).currencyCode,
                      )}
                    </span>

                    {product.priceModifications?.map((modification) => (
                      <React.Fragment key={getPriceModificationKey(product.id, modification)}>
                        <span>{getPriceModificationLabel(modification.name)}</span>
                        <span className="text-right">
                          {formatSignedPrice(
                            formatPrice,
                            modification.value,
                            modification.isNegative,
                          )}
                        </span>
                      </React.Fragment>
                    ))}

                    {product.aplus &&
                      product.aplus.length > 0 &&
                      product.aplus.map((service) => (
                        <React.Fragment key={service.name}>
                          <span>{service.name}</span>
                          <span className="text-right">
                            {formatPrice(service.finalPrice.value, service.finalPrice.currencyCode)}
                          </span>
                        </React.Fragment>
                      ))}

                    <span className="font-bold">{formatMessage(messages.endPriceLabel)}</span>
                    <span className="text-right font-bold">
                      {formatPrice(product.finalPrice?.value, product.finalPrice?.currencyCode)}
                    </span>
                  </div>
                </div>
              ))}

              <Divider className="my-1" />

              <div className="flex flex-col gap-0.5 px-1.5">
                <span className="font-bold">{formatMessage(messages.title)}</span>

                <div className="grid grid-cols-[1fr_auto] gap-x-2">
                  <span>{formatMessage(messages.subtotalLabel)}</span>
                  <span className="text-right">
                    {formatPrice(invoice.subTotal?.value, invoice.subTotal?.currencyCode)}
                  </span>

                  {invoice.priceModifications?.map((modification) => (
                    <React.Fragment key={modification.name}>
                      <span>{modification.name}</span>
                      <span className="text-right">
                        {modification.isNegative ? '-' : '+'}{' '}
                        {formatPrice(modification.value?.value, modification.value?.currencyCode)}
                      </span>
                    </React.Fragment>
                  ))}

                  <span className="font-bold">{formatMessage(messages.grandTotalLabel)}</span>
                  <span className="text-right font-bold">
                    {formatPrice(invoice.grandTotal?.value, invoice.grandTotal?.currencyCode)}
                  </span>
                </div>
              </div>
            </div>
          )}
          {invoice.paymentPlan && invoice.paymentPlan.length > 0 && (
            <div className="mb-1 grid grid-cols-2 gap-0.5 text-right">
              <span>{formatMessage(messages.paymentPlanLabel)}</span>
              <span />
              {invoice.paymentPlan.map((payment: any) => (
                <span key={`payment-text-${payment.text}`}>{payment.text}</span>
              ))}
              {invoice.paymentPlan.map((payment: any) => (
                <span key={`payment-unknown-${payment.text}`}>???</span>
              ))}
            </div>
          )}
          <div className="my-1 flex flex-col gap-0.5">
            {invoice.showEffectiveInstallmentsHint && (
              <span className="wrap-break-words">
                <FormattedMessage
                  {...messages.originalRateText}
                  values={{
                    link: (chunks) => (
                      <Link className="underline" href="https://to.be.done">
                        {chunks}
                      </Link>
                    ),
                  }}
                />
              </span>
            )}
            {invoice.paymentHint && (
              <span className="wrap-break-words my-1">
                {parseBackendMessage(invoice.paymentHint)}
              </span>
            )}
          </div>
          <div className="w-full md:mx-auto md:max-w-[90%]">
            <AccountInformation
              referenceCode={invoice.paymentIntendedUse}
              showBankConnection={invoice.showBankConnection}
            />
          </div>
          <span className="text-color-text-darktransparent text-size-headline-sm my-1 leading-none">
            {`${legalName}, ${streetAddress}, ${postalCode} ${addressLocality}, ${formatMessage(messages.companyAddressVATInfo)} `}
            <br />
            <span>
              {formatMessage(messages.termsText, {
                link: (chunks) => (
                  <Link className="underline" href={chunks.join('')}>
                    {chunks}
                  </Link>
                ),
              })}
            </span>
            <br />
            {formatMessage(messages.ownershipNote)}
          </span>
        </div>
        <InvoiceDownloadButton invoiceNumber={invoiceNumber} />

        {/* Reimbursements section - shown after download button */}
        {invoice.reimbursements && invoice.reimbursements.entries.length > 0 && (
          <div className="mt-2">
            <div className="mb-1 px-1.5">
              <h3 className="text-size-headline-xl mb-1">
                {formatMessage(messages.reimbursementsTitle)}
              </h3>
            </div>

            {/* Reimbursement entries - card layout responsive for all screen sizes */}
            {invoice.reimbursements.entries.map((reimbursement, index) => (
              <div
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={reimbursement.orderNumber || `reimbursement-${index}`}
                className={`mb-1 px-1.5 py-2 ${index % 2 === 0 ? 'bg-color-grey-light' : ''}`}
              >
                {reimbursement.orderNumber ? (
                  /* With article reference - 3-column grid */
                  <div className="flex flex-col gap-2 md:grid md:grid-cols-[auto_1fr_auto]">
                    {/* Image and product details row (mobile) / columns (desktop) */}
                    <div className="flex gap-2 md:contents">
                      {/* Column 1: Image */}
                      <div className="h-20 w-20 shrink-0">
                        <Image
                          width={72}
                          height={72}
                          src={reimbursement.imageUrl || ''}
                          alt={reimbursement.name}
                        />
                      </div>

                      {/* Column 2: Product details with date */}
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-bold wrap-break-word">{reimbursement.name}</span>
                        <span className="text-size-body-sm">
                          {formatMessage(messages.articleNumberLabel)} {reimbursement.orderNumber}
                        </span>
                        {reimbursement.variation && (
                          <span className="text-size-body-sm">{reimbursement.variation}</span>
                        )}
                        {reimbursement.quantity && (
                          <span className="text-size-body-sm">
                            {formatMessage(messages.quantityLabel)} {reimbursement.quantity}
                          </span>
                        )}
                        <span className="text-size-body-sm">
                          {formatMessage(messages.reimbursementDateLabel)}{' '}
                          {reimbursement.dateFormatted}
                        </span>
                      </div>
                    </div>

                    {/* Column 3: Label and amount value in grid layout like invoice pricing */}
                    <div className="text-size-body-sm mt-2 grid grid-cols-[1fr_auto] gap-x-2 md:mt-0 md:grid md:grid-cols-[auto_auto] md:content-start md:gap-x-2 md:gap-y-0.5">
                      <span className="font-bold md:text-right">
                        {formatMessage(messages.reimbursementAmountLabel)}
                      </span>
                      <span className="text-right font-bold">
                        {formatPrice(reimbursement.amount.value, reimbursement.amount.currencyCode)}
                      </span>
                    </div>
                  </div>
                ) : (
                  /* Without article reference - 2-column grid */
                  <div className="flex flex-col gap-2 md:grid md:grid-cols-[1fr_auto]">
                    {/* Column 1: Name with prefix and date */}
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="font-bold wrap-break-word">
                        {formatMessage(messages.reimbursementWithoutArticlePrefix)}{' '}
                        {reimbursement.name}
                      </span>
                      <span className="text-size-body-sm">
                        {formatMessage(messages.reimbursementDateLabel)}{' '}
                        {reimbursement.dateFormatted}
                      </span>
                    </div>

                    {/* Column 2: Label and amount value in grid layout */}
                    <div className="text-size-body-sm mt-2 grid grid-cols-[1fr_auto] gap-x-2 md:mt-0 md:grid md:grid-cols-[auto_auto] md:content-start md:gap-x-2 md:gap-y-0.5">
                      <span className="font-bold md:text-right">
                        {formatMessage(messages.reimbursementAmountLabel)}
                      </span>
                      <span className="text-right font-bold">
                        {formatPrice(reimbursement.amount.value, reimbursement.amount.currencyCode)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <Divider className="my-1" />

            {/* Credit note totals: surcharges/voucher correction, then sum and what stays open */}
            <div className="mt-1 flex justify-end p-1.5">
              <div className="text-size-body-sm grid grid-cols-[auto_auto] gap-x-2 gap-y-0.5 text-right">
                {invoice.reimbursements.priceModifications.map((modification) => (
                  <React.Fragment key={getPriceModificationSuffix(modification)}>
                    <span>{getPriceModificationLabel(modification.name)}</span>
                    <span>
                      {formatSignedPrice(formatPrice, modification.value, modification.isNegative)}
                    </span>
                  </React.Fragment>
                ))}

                <span className="font-bold">{formatMessage(messages.reimbursementsSumLabel)}</span>
                <span className="font-bold">
                  {formatPrice(
                    invoice.reimbursements.total.value,
                    invoice.reimbursements.total.currencyCode,
                  )}
                </span>

                {openAmount !== null && (
                  <>
                    <span className="font-bold">
                      {formatMessage(messages.reimbursementsOpenLabel)}
                    </span>
                    <span className="font-bold">
                      {formatPrice(openAmount, invoice.reimbursements.total.currencyCode)}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Dialog onClose={onClose} isOpen className="max-w-[37.5rem]">
      <DialogHeader onClose={onClose}>{formatMessage(messages.title)}</DialogHeader>
      {fetching && renderLoadingContent()}
      {error && renderErrorContent()}
      {!fetching && !error && !invoice && renderInvoiceNotFoundError()}
      {!fetching && !error && invoice && renderInvoiceContent()}
    </Dialog>
  );
};
